import type { ComponentProps } from "react";
import { forwardRef } from "react";
import type { SizeTokens, TamaguiElement } from "tamagui";
import {
  createStyledContext,
  styled,
  ThemeableStack,
  withStaticProperties,
} from "tamagui";

interface AlignCells {
  y: "center" | "start" | "end";
  x: "center" | "start" | "end";
}

type AlignHeaderCells = AlignCells;

const TableContext = createStyledContext<{
  cellWidth: SizeTokens | number;
  cellHeight: SizeTokens | number;
  alignHeaderCells: {
    y: "center" | "start" | "end";
    x: "center" | "start" | "end";
  };
  alignCells: {
    y: "center" | "start" | "end";
    x: "center" | "start" | "end";
  };
  borderColor: string;
}>({
  cellWidth: "$8",
  cellHeight: "$8",
  alignHeaderCells: { x: "start", y: "center" },
  alignCells: { x: "center", y: "center" },
  borderColor: "$gray-200",
});

const StyledRow = styled(ThemeableStack, {
  tag: "tr",
  flexDirection: "row",
  context: TableContext,
  variants: {
    selected: {
      true: {
        backgroundColor: "$gray-100",
      },
      false: {},
    },
    rowLocation: {
      first: () => {
        return {
          borderBottomWidth: "$0.2",
        };
      },
      last: () => {
        return {
          borderBottomWidth: 0,
          borderBottomLeftRadius: "$1",
          borderBottomRightRadius: "$1",
        };
      },
      middle: () => {
        return {
          borderBottomWidth: "$0.2",
        };
      },
    },
  },
});

type RowProps = ComponentProps<typeof StyledRow>;

const Row = forwardRef<TamaguiElement, RowProps>((props, ref) => (
  <StyledRow ref={ref} {...props} />
));

const Cell = styled(ThemeableStack, {
  tag: "td",
  flexDirection: "row",
  context: TableContext,
  flexGrow: 0,
  flexShrink: 1,
  flex: 1,
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "flex-start",
  paddingHorizontal: "$2",
  height: "$5.8",

  variants: {
    cellWidth: {
      "...size": (name, { tokens }) => {
        return {
          //@ts-expect-error love tamagui
          width: tokens.size[name],
        };
      },
    },
    cellHeight: {
      "...size": (name, { tokens }) => {
        return {
          //@ts-expect-error love tamagui
          minHeight: tokens.size[name],
        };
      },
    },
    alignCells: (val: AlignCells) => {
      return {
        alignItems: val.y === "center" ? "center" : `flex-${val.y}`,
        justifyContent: val.x === "center" ? "center" : `flex-${val.x}`,
      };
    },
    cellLocation: {
      first: () => {
        return {
          borderLeftWidth: 0,
        };
      },
      last: () => {
        return {
          borderLeftWidth: "$0.2",
        };
      },
      middle: () => {
        return {
          borderLeftWidth: "$0.2",
        };
      },
    },
  } as const,
});

const HeaderCell = styled(ThemeableStack, {
  tag: "th",
  flexDirection: "row",
  context: TableContext,
  flexGrow: 0,
  flexShrink: 1,
  flex: 1,
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "flex-start",
  paddingHorizontal: "$2",
  height: "$5.8",

  variants: {
    cellWidth: {
      "...size": (name, { tokens }) => {
        return {
          //@ts-expect-error love tamagui
          width: tokens.size[name],
        };
      },
    },

    alignHeaderCells: (val: AlignHeaderCells) => {
      return {
        alignItems: val.y === "center" ? "center" : `flex-${val.y}`,
        justifyContent: val.x === "center" ? "center" : `flex-${val.x}`,
      };
    },

    cellLocation: {
      first: () => {
        return {
          borderTopLeftRadius: "$1",
        };
      },
      last: () => {
        return {
          borderLeftWidth: "$0.2",
          borderTopRightRadius: "$1",
        };
      },
      middle: () => {
        return {
          borderLeftWidth: "$0.2",
        };
      },
    },
  } as const,
});

const TableBody = styled(ThemeableStack, {
  tag: "tbody",
  flexDirection: "column",
  context: TableContext,
  flexShrink: 1,
});

const TableHead = styled(ThemeableStack, {
  tag: "thead",
  flexDirection: "column",
  context: TableContext,
  flexShrink: 1,
});

const TableFoot = styled(ThemeableStack, {
  tag: "tfoot",
  flexDirection: "column",
  context: TableContext,
  flexShrink: 1,
});

const TableComp = styled(ThemeableStack, {
  tag: "table",
  context: TableContext,
  borderWidth: "$0.2",
  backgrounded: true,
  borderColor: "$gray-200",
  borderRadius: "$1",
  width: "100%",
  variants: {
    cellWidth: {
      "...size": () => {
        return {};
      },
    },
    cellHeight: {
      "...size": () => {
        return {};
      },
    },
    alignHeaderCells: (_val: unknown) => ({}),
    alignCells: (_val: unknown) => ({}),
  },
});

export const Table = withStaticProperties(TableComp, {
  Head: TableHead,
  Body: TableBody,
  Row,
  Cell,
  HeaderCell,
  Foot: TableFoot,
});
